import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { endpoints } from '@/api/endpoints';
import { ApiError, getToken, setToken as persistToken } from '@/api/client';
import type { Permission, Self } from '@/api/types';

/**
 * office-ui-v1 epic (ADR-064). The backend has no password/SSO login flow -
 * ADR-061 built bearer tokens only (see scripts/office/office-cli.ts's
 * header). The office UI's login screen is therefore a token-paste screen,
 * not a password form, and this is the ONE place a token is stored
 * (localStorage) or read for every subsequent request.
 *
 * `self` is fetched once per token and cached in memory; every RBAC
 * decision in this app (nav visibility, action buttons, route guards) reads
 * from `self.permissions`, which is the exact same
 * packages/foundation/src/authz/permissions.ts matrix the server enforces -
 * this is a courtesy UI layer ONLY. The server's requirePermission() is the
 * real boundary; losing this file entirely would change what the UI shows,
 * never what a request is allowed to do.
 */

type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous' }
  | { status: 'authenticated'; self: Self }
  | { status: 'error'; error: unknown };

type AuthContextValue = {
  readonly state: AuthState;
  readonly login: (token: string) => Promise<void>;
  readonly logout: () => void;
  readonly hasPermission: (permission: Permission) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const loadSelf = useCallback(async (): Promise<void> => {
    const token = getToken();
    if (!token) {
      setState({ status: 'anonymous' });
      return;
    }
    try {
      const self = await endpoints.self();
      setState({ status: 'authenticated', self });
    } catch (err) {
      // An expired/revoked/malformed token: fall back to the login screen
      // rather than an error page - a stale token is an expected, recoverable
      // state, not a bug.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        persistToken(null);
        setState({ status: 'anonymous' });
      } else {
        setState({ status: 'error', error: err });
      }
    }
  }, []);

  useEffect(() => {
    void loadSelf();
  }, [loadSelf]);

  const login = useCallback(
    async (token: string): Promise<void> => {
      persistToken(token);
      await loadSelf();
    },
    [loadSelf],
  );

  const logout = useCallback((): void => {
    // ADR-084: an interactive sign-in is also ended on the server (best effort -
    // the device forgets it either way). A service key is never revoked from here.
    const wasSession = state.status === 'authenticated' && state.self.auth_kind === 'SESSION';
    if (wasSession) void endpoints.logout().catch(() => undefined);
    persistToken(null);
    setState({ status: 'anonymous' });
  }, [state]);

  const hasPermission = useCallback(
    (permission: Permission): boolean => state.status === 'authenticated' && state.self.permissions.includes(permission),
    [state],
  );

  const value = useMemo<AuthContextValue>(() => ({ state, login, logout, hasPermission }), [state, login, logout, hasPermission]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() called outside <AuthProvider>');
  return ctx;
}
